import "@/styles/index.css";
import { StrictMode, Suspense, lazy, useEffect } from "react";
import { preload, preconnect } from "react-dom";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query";
import { I18nextProvider } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import { checkEnvVars } from "@/utils/checkEnvVars";
import { requiredEnvVars } from "@/constants/requiredEnvVars";
import { IntlProvider } from "react-intl";
import i18n from "../i18n";
import useLocale from "./hooks/useLocale";
import { router } from "@/routing/router";

const ReactQueryDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
    import("@tanstack/react-query-devtools").then((d) => ({
      default: d.ReactQueryDevtools,
    }))
  );

// Check for required environment variables
checkEnvVars(import.meta.env, requiredEnvVars);

const App = () => {

  // Hooks
  const currentLocale = useLocale();

  // Preload resources
  // Preload Google Fonts
  preconnect("https://fonts.googleapis.com");
  preconnect("https://fonts.gstatic.com", { crossOrigin: "anonymous" });

  // Preload Montserrat font files (main weights)
  preload("https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;400;500;600;700;800;900&display=swap", { as: "style" });
  preload("https://fonts.googleapis.com/css2?family=Montserrat+Alternates:wght@100;200;400;500;600;700;800;900&display=swap", { as: "style" });

  // Ensure preloaded fonts are actually used
  useEffect(() => {
    const montserratLink = document.createElement("link");
    montserratLink.rel = "stylesheet";
    montserratLink.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;400;500;600;700;800;900&display=swap";

    const montserratAltLink = document.createElement("link");
    montserratAltLink.rel = "stylesheet";
    montserratAltLink.href = "https://fonts.googleapis.com/css2?family=Montserrat+Alternates:wght@100;200;400;500;600;700;800;900&display=swap";

    document.head.appendChild(montserratLink);
    document.head.appendChild(montserratAltLink);

    return () => {
      document.head.removeChild(montserratLink);
      document.head.removeChild(montserratAltLink);
    };
  }, []);

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
        <IntlProvider locale={currentLocale}>
          <I18nextProvider i18n={i18n}>
            <RouterProvider router={router} />
          </I18nextProvider>
        </IntlProvider>
      </QueryClientProvider>
    </StrictMode>
  );
};

export default App;
